import $api from '@/http';
import type { IDocumentScheme } from '@models/document/IDocumentScheme';
import type { IDocumentDraft } from '@models/documentDraft/IDocumentDraft';

/**
 * Происхождение черновика для строки списка: из шаблона или на базе другого документа.
 *
 * В строке видно только имя и время, поэтому два черновика одного действия с разным
 * происхождением неразличимы — а это разная работа. Идентификаторы из `templateId`/`baseDocId`
 * пользователю ничего не говорят, поэтому имя тянем с сервера — по одной схеме на идентификатор.
 */
export interface IDraftOrigin {
    kind: 'template' | 'base';
    id: string;
}

export const draftOriginRef = (draft: Pick<IDocumentDraft, 'templateId' | 'baseDocId'>): IDraftOrigin | null => {
    if (draft.templateId) return { kind: 'template', id: draft.templateId };
    if (draft.baseDocId) return { kind: 'base', id: draft.baseDocId };
    return null;
};

export const draftOriginKey = (origin: IDraftOrigin): string => `${origin.kind}:${origin.id}`;

/** Без имени пометка всё равно осмысленна — её и показываем, пока имя едет (или если не доехало). */
export const draftOriginLabel = (origin: IDraftOrigin, title?: string): string => {
    if (origin.kind === 'template') return title ? `из шаблона «${title}»` : 'из шаблона';
    return title ? `на основании: ${title}` : 'на основании документа';
};

// Имена живут дольше списка: поповер открывают по многу раз за сессию, а шаблоны и базовые
// документы у одного пользователя повторяются. undefined в кэше — «спросили, сервер не назвал».
const titles = new Map<string, string | undefined>();
const inFlight = new Map<string, Promise<string | undefined>>();

// Шаблон — тоже документ, отличается флагом схемы, поэтому одного запроса хватает обоим: у шаблона
// берём его имя, у базового документа — название потока. Регистрационный номер потребовал бы
// второго запроса за formData, а в списках карточки базовый документ всегда один — её собственный.
const requestTitle = async (origin: IDraftOrigin): Promise<string | undefined> => {
    const { data } = await $api.get<IDocumentScheme>(`/document/scheme/${encodeURIComponent(origin.id)}`, {
        suppressErrorNotification: true,
        timeout: 10000,
    });
    return (data.isTemplate ? data.templateName : data.flowTitle)?.trim() || undefined;
};

/** Имя происхождения: из кэша либо одним запросом на идентификатор. Отказ — тоже ответ (undefined). */
export const loadDraftOriginTitle = (origin: IDraftOrigin): Promise<string | undefined> => {
    const key = draftOriginKey(origin);
    if (titles.has(key)) return Promise.resolve(titles.get(key));
    const pending = inFlight.get(key);
    if (pending) return pending;
    const request = requestTitle(origin)
        .catch(() => undefined)
        .then((title) => {
            titles.set(key, title);
            inFlight.delete(key);
            return title;
        });
    inFlight.set(key, request);
    return request;
};

/** @internal Только для тестов: кэш общий на модуль и переживает перемонтирование списка. */
export const resetDraftOriginTitles = () => {
    titles.clear();
    inFlight.clear();
};
